import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getAssistantConfig } from "../../actions/assistants";
import { RobotIcon } from "../Auth/onboarding/icons";
import Sidebar from "../../Components/Sidebar";
import Topbar from "../../Components/Topbar";
import ChatTester from "./ChatTester";
import AssistantProfileCard from "./AssistantProfileCard";
import SuggestedPromptsCard from "./SuggestedPromptsCard";
import TestingTipCard from "./TestingTipCard";
import LoadingState from "../../Components/LoadingState";

export default function Assistant() {
  // Set by the sidebar switcher and mirrored to localStorage, so the page
  // follows whichever assistant is currently selected.
  const assistantId = useSelector((state) => state.assistants.activeId);
  // The record the sidebar already loaded via GET /assistants — it carries the
  // sourceUrl and status the config payload doesn't, at no extra request.
  const assistant = useSelector((state) =>
    state.assistants.list.find((item) => item.id === state.assistants.activeId),
  );
  const dispatch = useDispatch();
  const chatRef = useRef(null);

  const [config, setConfig] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // GET /assistants/:id/config is the only call this page makes.
  useEffect(() => {
    if (!assistantId) {
      setConfig(null);
      setError("");
      return undefined;
    }

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      const result = await dispatch(getAssistantConfig(assistantId));
      if (cancelled) return;

      setLoading(false);

      if (result?.success) {
        setConfig(result.config);
        setError("");
        return;
      }

      setError(result?.message || "We couldn't load this assistant.");
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [assistantId, dispatch]);

  // Swapping assistants remounts the tester so its thread doesn't carry over.
  const testerKey = assistantId || "none";

  const renderBody = () => {
    if (!assistantId) {
      return (
        <div className="asst-list-state">
          <span className="asst-list-empty-icon">
            <RobotIcon />
          </span>
          <strong>No assistant selected</strong>
          <p>Pick one from the switcher at the top of the sidebar.</p>
        </div>
      );
    }

    if (loading && !config) {
      return (
        <LoadingState title="Loading assistant" />
      );
    }

    if (error) {
      return (
        <div className="asst-list-state">
          <p className="auth-error">{error}</p>
        </div>
      );
    }

    return (
      <div className="asst-grid">
        <ChatTester
          key={testerKey}
          ref={chatRef}
          assistant={assistant}
          config={config}
        />

        <div className="asst-side-col">
          <AssistantProfileCard assistant={assistant} config={config} />
          <SuggestedPromptsCard
            onSelect={(text) => chatRef.current?.sendMessage(text)}
          />
          <TestingTipCard />
        </div>
      </div>
    );
  };

  return (
    <div className="dash">
      <Sidebar />

      <div className="dash-main">
        <Topbar
          title={config?.name || assistant?.name || "Assistant"}
          subtitle="Test how your AI assistant responds to real conversations."
        />

        <div className="dash-content">{renderBody()}</div>
      </div>
    </div>
  );
}
